/* @ngInject */

export default (
  $rootScope,
  helperService,
  helperPdfService,
  incomeService,
  householdsService
) => {
  /**
   * Prepare ability to service details.
   *
   * @param {Object} businessIncome
   * @param {String} businessYear
   *
   * @returns {Object}
   */
  const totalBusinessIncome = ({ businessIncome, businessYear }) => {
    let total = 0;
    if (businessIncome && businessYear) {
      total =
        helperService.isNumber(businessIncome.business[businessYear].profitBeforeTax, true) +
        helperService.isNumber(businessIncome.business[businessYear].depreciation, true) +
        helperService.isNumber(
          businessIncome.business[businessYear].interestAndOtherAddbacks,
          true
        ) +
        helperService.isNumber(businessIncome.business[businessYear].leaseRentals, true) +
        helperService.isNumber(businessIncome.business[businessYear].amortisation, true) +
        helperService.isNumber(
          businessIncome.business[businessYear].lossOnSaleOfFixedAsset,
          true
        ) -
        helperService.isNumber(
          businessIncome.business[businessYear].profitOnSaleOfFixedAsset,
          true
        ) +
        helperService.isNumber(businessIncome.business[businessYear].nonRecurringExpense, true);
    }
    return total;
  };

  /**
   * Prepare ability to service details.
   *
   * @param {Array} contacts
   * @param {Array} contactLabels
   * @param {Object} ticket
   * @param {Object} loan
   *
   * @returns {Object}
   */
  const prepareAbilityToServiceDetails = ({
    contacts,
    contactLabels,
    ticket,
    workflow,
    loan
  }) => {
    const content = [];
    const preferredProduct = helperService.getPreferredProduct({
      products: loan?.compareProducts || []
    });
    if (
      contacts &&
      ticket &&
      loan?.isCommercial &&
      preferredProduct &&
      (_.indexOf(preferredProduct?.selectedProduct?.assetFinance?.loans || [], 'FULL_DOC') >
        -1 ||
        _.indexOf(preferredProduct?.selectedProduct?.assetFinance?.loans || [], 'SELF_DEC') >
          -1)
    ) {
      content.push(
        helperPdfService.createHeader({
          title: 'ABILITY TO SERVICE',
          pageBreak: 'before'
        })
      );
      let overallSurplusLast = 0;
      let overallSurplusPrevious = 0;
      const totalExistingRepayments = _.multiply(
        helperService.getTotalExistingLiabilitiesRepayments({
          liabilities: ticket?.liabilities || [],
          assetCommitmentSchedules: ticket?.assetCommitmentSchedules || [],
          contacts,
          workflow,
          isCurrent: true
        }),
        12
      );
      const newRepayments = _.multiply(loan?.financeProposal?.estimatedRepayment, 12);
      const totalExpenses = _.multiply(
        householdsService.getTotalExpenses({ expenses: ticket?.expenses || [] }),
        12
      );
      let lastTaxYear = 'N/A';
      let previousTaxYear = 'N/A';
      const availableIncomeTypes = incomeService.getAvailableIncomeTypes();
      const len = contacts.length;
      for (let c = 0; c < len; c += 1) {
        const contact = contacts[c];
        const contactType = helperService.getContactTypeInDeal({
          ticket,
          contactLabels,
          idContact: contact.id
        });
        const filteredIncome = _.filter(ticket?.income, (obj) => obj.idContact === contact.id);
        const businessIncome = _.find(
          filteredIncome,
          (i) => i.incomeType.id === availableIncomeTypes[1].id
        );
        if (businessIncome && contact.isCompany) {
          lastTaxYear =
            lastTaxYear === 'N/A'
              ? businessIncome?.business?.lastTaxYear?.taxYear || 'N/A'
              : lastTaxYear;
          previousTaxYear =
            previousTaxYear === 'N/A'
              ? businessIncome?.business?.previousTaxYear?.taxYear || 'N/A'
              : previousTaxYear;
          const totalLast = totalBusinessIncome({
            businessIncome,
            businessYear: 'lastTaxYear'
          });
          const totalPrevious = totalBusinessIncome({
            businessIncome,
            businessYear: 'previousTaxYear'
          });
          const otherIncome = _.filter(
            filteredIncome,
            (i) => i.incomeType.id === availableIncomeTypes[2].id
          );
          const totalOtherIncome = _.reduce(
            otherIncome,
            (memo, num) => memo + helperService.isNumber(num.grossValue, true),
            0
          );
          const totalNetCashLast = totalLast + totalOtherIncome;
          const totalNetCashPrevious = totalPrevious + totalOtherIncome;
          const surplusDeficitLast =
            totalNetCashLast -
            totalExistingRepayments -
            (contactType !== 'Guarantor' ? newRepayments : 0);
          const surplusDeficitPrevious =
            totalNetCashPrevious -
            totalExistingRepayments -
            (contactType !== 'Guarantor' ? newRepayments : 0);
          overallSurplusLast += surplusDeficitLast;
          overallSurplusPrevious += surplusDeficitPrevious;
          content.push(
            helperPdfService.createSubHeader({
              title: `${contact.getName().toUpperCase()} - ${contactType.toUpperCase()}`
            })
          );
          content.push({
            table: {
              widths: ['48%', '4%', '48%'],
              body: [
                [
                  helperPdfService.createSubHeader({
                    title: `Last tax year - ${lastTaxYear}`,
                    bodyStyle: ['subSubHeader']
                  }),
                  '',
                  helperPdfService.createSubHeader({
                    title: `Previous tax year - ${previousTaxYear}`,
                    bodyStyle: ['subSubHeader']
                  })
                ],
                [
                  {
                    text: [
                      { text: 'Gross sales\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.grossSales.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Gross sales\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.grossSales.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Profit before tax\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.profitBeforeTax.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Profit before tax\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.profitBeforeTax.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Depreciation\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.depreciation.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Depreciation\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.depreciation.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Interest and other addbacks\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.interestAndOtherAddbacks.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Interest and other addbacks\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.interestAndOtherAddbacks.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Lease rentals\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.leaseRentals.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Lease rentals\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.leaseRentals.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Amortisation\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.amortisation.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Amortisation\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.amortisation.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Non recurring expense\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.nonRecurringExpense.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Non recurring expense\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.nonRecurringExpense.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Loss on sale of fixed asset\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.lossOnSaleOfFixedAsset.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Loss on sale of fixed asset\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.lossOnSaleOfFixedAsset.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Profit on sale of fixed asset\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.profitOnSaleOfFixedAsset.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Profit on sale of fixed asset\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.profitOnSaleOfFixedAsset.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Profit on sale of fixed asset\n', style: 'label' },
                      {
                        text: businessIncome?.business?.lastTaxYear?.profitOnSaleOfFixedAsset.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Profit on sale of fixed asset\n', style: 'label' },
                      {
                        text: businessIncome?.business?.previousTaxYear?.profitOnSaleOfFixedAsset.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Other income\n', style: 'label' },
                      {
                        text: totalOtherIncome.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Other income\n', style: 'label' },
                      {
                        text: totalOtherIncome.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Net cash available\n', style: 'label' },
                      {
                        text: totalNetCashLast.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Net cash available\n', style: 'label' },
                      {
                        text: totalNetCashPrevious.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ]
              ]
            },
            layout: 'noBordersNoPadding'
          });
          content.push({
            table: {
              widths: ['48%', '4%', '48%'],
              body: [
                [
                  helperPdfService.createSubHeader({
                    title: 'Less',
                    bodyStyle: ['subSubHeader']
                  }),
                  '',
                  helperPdfService.createSubHeader({
                    title: 'Less',
                    bodyStyle: ['subSubHeader']
                  })
                ],
                [
                  {
                    text: [
                      { text: 'Existing loan repayments\n', style: 'label' },
                      {
                        text: totalExistingRepayments.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Existing loan repayments\n', style: 'label' },
                      {
                        text: totalExistingRepayments.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ]
              ]
            },
            layout: 'noBordersNoPadding'
          });
          if (contactType !== 'Guarantor') {
            content[content.length - 1].table.body[2] = ['', '', ''];
            content[content.length - 1].table.body[2][0] = {
              text: [
                { text: 'New loan repayments\n', style: 'label' },
                {
                  text: newRepayments.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ],
              style: 'tableCellMargin'
            };
            content[content.length - 1].table.body[2][2] = {
              text: [
                { text: 'New loan repayments\n', style: 'label' },
                {
                  text: newRepayments.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ],
              style: 'tableCellMargin'
            };
          }
          content.push({
            table: {
              widths: ['48%', '4%', '48%'],
              body: [
                [
                  helperPdfService.createSubHeader({
                    title: '',
                    bodyStyle: ['subSubHeader']
                  }),
                  '',
                  helperPdfService.createSubHeader({
                    title: '',
                    bodyStyle: ['subSubHeader']
                  })
                ],
                [
                  {
                    text: [
                      { text: 'Surplus/Deficit\n', style: 'label' },
                      {
                        text: surplusDeficitLast.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Surplus/Deficit\n', style: 'label' },
                      {
                        text: surplusDeficitPrevious.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ]
              ]
            },
            layout: 'noBordersNoPadding'
          });
        }
        if (!contact.isCompany) {
          const netIncome = incomeService.getTotalNetIncomeByContact({
            income: filteredIncome,
            mfpType: null,
            assets: ticket?.assets,
            homeLoanSecurities: [],
            idContact: contact?.id,
            workflow,
            isDealOwnerBroker: true,
            haveLendersExisting: true
          });
          overallSurplusLast += netIncome - totalExpenses - totalExistingRepayments;
          overallSurplusPrevious += netIncome - totalExpenses - totalExistingRepayments;
          content.push(
            helperPdfService.createSubHeader({
              title: `${contact.getName().toUpperCase()} - ${contactType.toUpperCase()}`
            })
          );
          content.push({
            table: {
              widths: ['48%', '4%', '48%'],
              body: [
                [
                  helperPdfService.createSubHeader({
                    title: `Last tax year - ${lastTaxYear}`,
                    bodyStyle: ['subSubHeader']
                  }),
                  '',
                  helperPdfService.createSubHeader({
                    title: `Previous tax year - ${previousTaxYear}`,
                    bodyStyle: ['subSubHeader']
                  })
                ],
                [
                  {
                    text: [
                      { text: 'Net income\n', style: 'label' },
                      {
                        text: netIncome.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Net income\n', style: 'label' },
                      {
                        text: netIncome.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Total expenses\n', style: 'label' },
                      {
                        text: totalExpenses.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Total expenses\n', style: 'label' },
                      {
                        text: totalExpenses.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Existing loan repayments\n', style: 'label' },
                      {
                        text: totalExistingRepayments.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  },
                  '',
                  {
                    text: [
                      { text: 'Existing loan repayments\n', style: 'label' },
                      {
                        text: totalExistingRepayments.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        ),
                        style: 'value'
                      }
                    ],
                    style: 'tableCellMargin'
                  }
                ]
              ]
            },
            layout: 'noBordersNoPadding'
          });
        }
      }
      content.push({
        table: {
          widths: ['48%', '4%', '48%'],
          body: [
            [
              helperPdfService.createSubHeader({
                title: '',
                bodyStyle: ['subSubHeader']
              }),
              '',
              helperPdfService.createSubHeader({
                title: '',
                bodyStyle: ['subSubHeader']
              })
            ],
            [
              {
                text: [
                  { text: 'Overall surplus\n', style: 'label' },
                  {
                    text: overallSurplusLast.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  }
                ],
                style: 'tableCellMargin'
              },
              '',
              {
                text: [
                  { text: 'Overall surplus\n', style: 'label' },
                  {
                    text: overallSurplusPrevious.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  }
                ],
                style: 'tableCellMargin'
              }
            ]
          ]
        },
        layout: 'noBordersNoPadding'
      });

      content.push({
        text: '',
        margin: [0, 20, 0, 0]
      });
      content.push(
        helperPdfService.createSubHeader({
          title: 'SUPPORTING COMMENTARY'
        })
      );
      content.push({
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: [
                  {
                    text: 'Ability to service/financial analysis\n',
                    style: 'label'
                  },
                  {
                    text: loan?.compliance?.comments?.abilityToServiceFinancialAnalysis || '-',
                    style: 'value'
                  }
                ]
              }
            ]
          ]
        },
        layout: 'noBorders'
      });
    }
    return content;
  };

  return {
    prepareAbilityToServiceDetails
  };
};
